"use client"

/**
 * Leo spotted, one finding at a time, on the classification card stack.
 *
 * **Why this shape.** The panel used to render every finding as a stacked
 * list under one "Enhance with Leo" button. That is two problems at once:
 * the author reads four defects and takes none of them (the PRD calls this
 * banner blindness), and the single button bundles all four into one ask, so
 * what Leo changes is Leo's choice rather than theirs. One finding per card
 * makes the CTA specific enough to be a decision — "Replace All of the
 * above" instead of "fix whatever you found".
 *
 * **The same pieces as everything else Leo.** `LeoCardStack` +
 * `LeoCardStackNavigation` laid out the way `LeoClassificationCard` lays
 * them out, and `AskLeoButton` for the CTA. Nothing here is a one-off.
 *
 * **The wash sits behind the stack, not inside the front card.** A blob
 * clipped to one card reads as decoration on that card; behind the whole
 * stack it reads as the surface Leo is working on, and the peek layers of
 * the cards still to come sit in it.
 *
 * **Skip, and nothing else.** No Dismiss: an author deferring a finding is
 * rarely claiming it is wrong, and a persistent dismiss quietly deletes
 * work nobody decided was unnecessary. A skipped or resolved card stays
 * in the stack without a status tag. Resolving one turns that card into
 * All good in place.
 */

import * as React from "react"

import { AskLeoButton } from "@/components/ask-leo-button"
import { StatusCell } from "@/components/data-views"
import {
  LeoCardStack,
  LeoCardStackNavigation,
} from "@/components/leo-card-stack"
import { Button } from "@/components/ui/button"
import { CardContent } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { LeoWorkStatus } from "@/components/assessment-structure/leo-work-status"
import { LeoSpottedBlobLayer } from "@/components/question-quality/leo-spotted-blob-layer"
import { LeoIcon } from "@/components/ui/leo-icon"
import { cn } from "@/lib/utils"
import {
  LIBRARY_AI_SPOTTED_LABEL,
  LIBRARY_AI_SPOTTED_PRESENTATION,
  type LibraryAiSpotted,
} from "@/lib/library-ai-spotted"
import {
  leoSpottedAllCardsSkipped,
  leoSpottedDeferredCollapseTitle,
  openLeoSpottedCards,
  type LeoSpottedCard,
  type LeoSpottedQueue,
} from "@/lib/question-quality/leo-spotted-queue"

/** The row's badge, on the card, so the two never read differently. */
/** Two actions share a row. A narrow card wraps the second one underneath. */
const SPOTTED_ACTION_CLASS =
  "min-w-[8.5rem] flex-[1_1_calc(50%-0.375rem)] justify-center"

/** A finished line, not the finding that used to be open. */
function spottedFixedLine(issue: { id: string; label: string; cover?: string }): string | null {
  const text = `${issue.id} ${issue.cover ?? ""} ${issue.label}`.toLowerCase()
  if (/\bpbi\b|separat|performance/.test(text)) return "Performance reviewed"
  if (/choice|distractor/.test(text)) return "Choices rewritten"
  if (/\brationale\b|\bexplanation\b/.test(text)) return "Rationale added"
  if (/passage|reference|exhibit/.test(text)) return "Reference added"
  if (/\bstem\b|\bformat\b|\bpairing\b/.test(text)) return "Stem rewritten"
  if (/overuse|exposure|used in/.test(text)) return "Exposure handled"
  if (/bloom/.test(text)) return "Bloom's set"
  if (/difficulty/.test(text)) return "Difficulty set"
  if (/category/.test(text)) return "Category mapped"
  if (/metadata|classification/.test(text)) return "Classification set"
  return null
}

function SpottedTagBadge({ tag }: { tag: LibraryAiSpotted | null | undefined }) {
  if (!tag) return null
  const presentation = LIBRARY_AI_SPOTTED_PRESENTATION[tag]
  return (
    <StatusCell
      label={LIBRARY_AI_SPOTTED_LABEL[tag]}
      tone={presentation.tone}
      icon={presentation.icon}
    />
  )
}

export function LeoSpottedQueueCard({
  queue,
  tag,
  onNavigate,
  onPrimary,
  onSkip,
  onReopen,
  secondary,
  disabled,
  /** Bloom's and difficulty chips. Replaces the metadata card copy. */
  levelFields,
  /** The quality fix is in flight. The card stays so it can turn into All good. */
  fixing,
  fixingLabel = "Improving this question",
  fixingDetail,
  className,
}: {
  queue: LeoSpottedQueue
  /** The AI Spotted badge from `readLeoSpotted`, shown on the card. */
  tag?: LibraryAiSpotted | null
  onNavigate: (index: number) => void
  onPrimary: (card: LeoSpottedCard) => void
  onSkip: (card: LeoSpottedCard) => void
  onReopen: (card: LeoSpottedCard) => void
  /** Optional second answer for this card, e.g. swap in a bank question. */
  secondary?: (card: LeoSpottedCard) => { label: string; onClick: () => void } | null
  disabled?: boolean
  levelFields?: React.ReactNode
  fixing?: boolean
  fixingLabel?: string
  fixingDetail?: string
  className?: string
}) {
  const [hovered, setHovered] = React.useState(false)
  const allDeferred = leoSpottedAllCardsSkipped(queue)
  const deferredTitle = leoSpottedDeferredCollapseTitle(queue)
  const [deferredExpanded, setDeferredExpanded] = React.useState(false)
  const wasAllDeferred = React.useRef(allDeferred)
  React.useEffect(() => {
    if (allDeferred && !wasAllDeferred.current) setDeferredExpanded(false)
    wasAllDeferred.current = allDeferred
  }, [allDeferred])

  const card = queue.cards[queue.activeIndex]
  if (!card || queue.cards.length === 0) return null
  const secondaryAction = secondary?.(card) ?? null
  const resolved = card.state === "resolved"
  const skipped = card.state === "skipped"
  const queueClear = openLeoSpottedCards(queue).length === 0
  const summary = resolved && (queueClear || card.id === "all-good")
  const fixedLabels = summary
    ? [...new Set(card.issues.map(spottedFixedLine).filter((line): line is string => Boolean(line)))]
    : []
  const showLevels = !resolved && card.kind === "metadata" && Boolean(levelFields)

  const stack = (
    <div
      className="relative isolate min-w-0"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {/* Behind the stack and bleeding past it, not clipped inside the
          front card. Unclipped on purpose: the blobs rest below their box
          (`bottom: -64`) so a wrapper with `overflow-hidden` cut them off
          entirely and the card rendered plain white. The blur and the
          opacity do the containing instead. Leo appearance ▸ Spotted card
          drives the size, opacity and placement. */}
      {resolved ? null : (
        <div className="pointer-events-none absolute -inset-x-6 -top-5 -bottom-12 z-0">
          <LeoSpottedBlobLayer wash="brand" className="overflow-visible" />
        </div>
      )}
      <div className="relative z-[1] min-w-0">
        <LeoCardStack
          count={queue.cards.length}
          index={queue.activeIndex}
          label="Leo spotted"
          className={cn(
            "relative border-brand/25",
            allDeferred ? undefined : className,
          )}
        >
          {/* `CardContent` carries the horizontal padding. Dropping it for
              a bare div is what left the copy flush against the card edge. */}
          <CardContent
            className={cn(
              "relative z-[1] flex min-w-0 flex-col gap-3",
              resolved && "leo-spotted-card-settle",
              disabled && "opacity-60",
            )}
          >
            {resolved ? (
              <div className="leo-spotted-settled flex min-w-0 flex-col gap-2.5" role="status">
                <div className="flex min-w-0 items-center gap-2.5">
                  <i
                    className="leo-spotted-check-pop fa-kit fa-circle-check-star-christmas shrink-0 text-[1.75rem] leading-none"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-foreground">
                      All good
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {summary ? "Nothing else needs a change." : card.header}
                    </span>
                  </span>
                  {summary ? null : (
                    <LeoCardStackNavigation
                      count={queue.cards.length}
                      index={queue.activeIndex}
                      onChange={onNavigate}
                      noun="card"
                      showCount={false}
                    />
                  )}
                </div>
                {fixedLabels.length > 0 ? (
                  <ul className="m-0 flex list-none flex-col gap-1.5 p-0">
                    {fixedLabels.map((label) => (
                      <li
                        key={label}
                        className="leo-spotted-fixed-line flex min-w-0 items-center gap-2 text-sm leading-5 text-foreground"
                      >
                        <i
                          className="fa-solid fa-check w-3.5 shrink-0 text-center text-[0.7rem] text-foreground"
                          aria-hidden
                        />
                        <span className="min-w-0">{label}</span>
                      </li>
                    ))}
                  </ul>
                ) : card.id === "not-checked" && card.body ? (
                  <p className="m-0 text-sm leading-5 text-muted-foreground">
                    {card.body}
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex min-w-0 items-center gap-2">
                <LeoIcon
                  variant="ambient"
                  size="xs"
                  state={hovered ? "invited" : "rest"}
                  ariaLabel=""
                  className="shrink-0"
                />
                <span className="shrink-0 text-xs font-semibold text-foreground">
                  Leo spotted
                </span>
                <span className="min-w-0 flex-1" />
                {skipped ? (
                  <span className="shrink-0 text-[0.6875rem] font-medium text-muted-foreground">
                    Skipped
                  </span>
                ) : (
                  <span className="min-w-0 shrink overflow-hidden">
                    <SpottedTagBadge tag={tag} />
                  </span>
                )}
                <span className="shrink-0">
                  <LeoCardStackNavigation
                    count={queue.cards.length}
                    index={queue.activeIndex}
                    onChange={onNavigate}
                    noun="card"
                    showCount={false}
                  />
                </span>
              </div>
            )}

            {resolved ? null : showLevels ? (
              <div className="flex min-w-0 flex-col gap-3">
                <div className="min-w-0">
                  <p className="m-0 text-[0.9375rem] font-semibold leading-snug text-foreground">
                    {card.header}
                  </p>
                  <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                    {card.body}
                  </p>
                </div>
                {levelFields}
              </div>
            ) : (
              <div className="min-w-0">
                <p className="m-0 text-[0.9375rem] font-semibold leading-snug text-foreground">
                  {card.header}
                </p>
                <p className="mt-1.5 text-sm leading-6 text-muted-foreground">
                  {card.body}
                </p>
              </div>
            )}

            {showLevels ? null : (
            <div className="flex min-w-0 flex-wrap gap-1.5 pt-0.5">
              {fixing && !resolved ? (
                <LeoWorkStatus label={fixingLabel} detail={fixingDetail} />
              ) : null}
              {resolved && card.id !== "all-good" ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={SPOTTED_ACTION_CLASS}
                  onClick={() => onReopen(card)}
                >
                  <i className="fa-light fa-arrow-rotate-left" aria-hidden="true" />
                  Reopen
                </Button>
              ) : null}
              {skipped && !resolved && !fixing && !showLevels ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(SPOTTED_ACTION_CLASS, "text-muted-foreground")}
                  disabled={disabled}
                  onClick={() => onReopen(card)}
                >
                  <i className="fa-light fa-arrow-rotate-left" aria-hidden="true" />
                  Reopen
                </Button>
              ) : null}
              {resolved || fixing || showLevels || !card.primaryLabel ? null : card.primaryAskLeo ? (
                <AskLeoButton
                  size="sm"
                  variant="outline"
                  label={card.primaryLabel}
                  showShortcut={false}
                  animatedStar
                  className={cn(SPOTTED_ACTION_CLASS, "bg-transparent")}
                  disabled={disabled}
                  onClick={() => onPrimary(card)}
                />
              ) : card.primarySolid ? (
                <Button
                  type="button"
                  size="sm"
                  className={SPOTTED_ACTION_CLASS}
                  disabled={disabled}
                  onClick={() => onPrimary(card)}
                >
                  {card.primaryLabel}
                </Button>
              ) : (
                <AskLeoButton
                  size="sm"
                  variant="outline"
                  label={card.primaryLabel}
                  showShortcut={false}
                  animatedStar
                  className={cn(SPOTTED_ACTION_CLASS, "bg-transparent")}
                  disabled={disabled}
                  onClick={() => onPrimary(card)}
                />
              )}
              {resolved || fixing || showLevels || !secondaryAction || card.primaryAskLeo ? null : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={SPOTTED_ACTION_CLASS}
                  disabled={disabled}
                  onClick={secondaryAction.onClick}
                >
                  {secondaryAction.label}
                </Button>
              )}
              {resolved || fixing || skipped || showLevels || card.hideSkip ? null : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className={cn(SPOTTED_ACTION_CLASS, "text-muted-foreground")}
                  disabled={disabled}
                  onClick={() => onSkip(card)}
                >
                  Skip
                </Button>
              )}
            </div>
            )}
          </CardContent>
        </LeoCardStack>
      </div>
    </div>
  )

  if (!allDeferred) return stack

  return (
    <Collapsible
      open={deferredExpanded}
      onOpenChange={setDeferredExpanded}
      className={cn("min-w-0 rounded-xl border border-brand/25 bg-card", className)}
    >
      <CollapsibleTrigger
        type="button"
        className="flex w-full min-w-0 items-center gap-2 px-4 py-3 text-start"
        aria-expanded={deferredExpanded}
        aria-label={
          deferredExpanded
            ? `Collapse Leo spotted, ${deferredTitle}`
            : `Expand Leo spotted, ${deferredTitle}`
        }
      >
        <LeoIcon variant="ambient" size="xs" state="rest" ariaLabel="" className="shrink-0" />
        <span className="shrink-0 text-xs font-semibold text-foreground">Leo spotted</span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {deferredTitle}
        </span>
        <span className="shrink-0 text-[0.6875rem] font-medium text-muted-foreground">
          Skipped
        </span>
        <span className="shrink-0 overflow-hidden">
          <SpottedTagBadge tag={tag} />
        </span>
        <i
          className={cn(
            "fa-light fa-chevron-down shrink-0 text-xs text-muted-foreground transition-transform",
            deferredExpanded && "rotate-180",
          )}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden border-t border-border/60 data-[state=open]:[animation:collapsible-down_200ms_ease-out] data-[state=closed]:[animation:collapsible-up_200ms_ease-out] motion-reduce:animate-none">
        <div className="px-1 pb-1 pt-0.5">{stack}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}
